/**
 * features/quotes/quotesWrite.ts
 * Quote writes. Drafts carry no number; sending reserves a gapless
 * QUO number and freezes the client + VAT snapshots. A quote can be
 * converted into a draft invoice (one click), after which it is
 * marked 'converted'.
 */
import { collection, deleteDoc, doc } from 'firebase/firestore';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { nextNumber } from '@/lib/numbering';
import { clientSnapshot, saveDraft as saveInvoiceDraft } from '@/features/invoices/invoicesWrite';
import {
  COLLECTIONS,
  type ClientSnapshot,
  type DocumentTotals,
  type LineItem,
  type Quote,
  type QuoteStatus,
  type VatSnapshot,
} from '@/types';

export { clientSnapshot };

export interface QuotePayload {
  clientId: string;
  client: ClientSnapshot;
  issueDate: string;
  validUntil: string;
  lineItems: LineItem[];
  totals: DocumentTotals;
  vat: VatSnapshot;
  notes?: string;
  terms?: string;
}

function base(p: QuotePayload) {
  return {
    clientId: p.clientId,
    client: p.client,
    issueDate: p.issueDate,
    validUntil: p.validUntil,
    lineItems: p.lineItems,
    totals: p.totals,
    currency: 'BWP' as const,
    vat: p.vat,
    notes: p.notes,
    terms: p.terms,
    updatedAt: new Date().toISOString(),
  };
}

export async function saveDraft(p: QuotePayload, actor: string, id?: string): Promise<string> {
  const data = { ...base(p), number: null, status: 'draft' as const };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.quotes, id), data);
    return id;
  }
  const ref = await addDoc(collection(db, COLLECTIONS.quotes), {
    ...data,
    createdAt: new Date().toISOString(),
    createdBy: actor,
  });
  return ref.id;
}

export async function sendQuote(p: QuotePayload, actor: string, id?: string): Promise<{ id: string; number: string }> {
  const number = await nextNumber('quote', new Date(p.issueDate));
  const now = new Date().toISOString();
  const data = { ...base(p), number, status: 'sent' as const, sentAt: now };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.quotes, id), data);
    return { id, number };
  }
  const ref = await addDoc(collection(db, COLLECTIONS.quotes), { ...data, createdAt: now, createdBy: actor });
  return { id: ref.id, number };
}

export async function setQuoteStatus(id: string, status: QuoteStatus): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.quotes, id), { status, updatedAt: new Date().toISOString() });
}

export async function deleteDraft(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.quotes, id));
}

/** Convert an (accepted/sent) quote into a DRAFT invoice for review. */
export async function convertToInvoice(quote: Quote, actor: string): Promise<string> {
  const issueDate = new Date().toISOString().slice(0, 10);
  const due = new Date();
  due.setDate(due.getDate() + 14);
  const dueDate = due.toISOString().slice(0, 10);

  const invoiceId = await saveInvoiceDraft(
    {
      clientId: quote.clientId,
      client: quote.client,
      issueDate,
      dueDate,
      lineItems: quote.lineItems,
      totals: quote.totals,
      vat: quote.vat,
      ...(quote.notes ? { notes: quote.notes } : {}),
    },
    actor,
  );

  await updateDoc(doc(db, COLLECTIONS.quotes, quote.id), {
    status: 'converted',
    convertedInvoiceId: invoiceId,
    updatedAt: new Date().toISOString(),
  });
  return invoiceId;
}
